import {useCallback, useState} from 'react';

import {ChemistryPreferences} from 'chemistry/ChemistryConstants';
import {ChemSearchSubmit, ChemSearchSubmitResult} from 'chemistry/chemSearchSubmit';
import {ToggleHighlightParams} from 'chemistry/ChemSearchUtils';
import preferences from 'Preferences';
import {ChemicalStructureSearchOperator} from 'search/SearchConstants';

interface UseChemSearchModalSubmitArgs {
  toggleHighlightParams: ToggleHighlightParams;
  highlightChecked: boolean;
  chemSearchOperator: ChemicalStructureSearchOperator;
  onRequestSubmit: (submit: ChemSearchSubmit) => void;
  buildSubmitResult: () => ChemSearchSubmitResult;
  onValidationError?: (result: Extract<ChemSearchSubmitResult, {type: 'validation_error'}>) => void;
}

interface UseChemSearchModalSubmitResult {
  submitError: string | null;
  clearSubmitError: () => void;
  handleSubmit: () => void;
}

export function useChemSearchModalSubmit({
  toggleHighlightParams,
  highlightChecked,
  chemSearchOperator,
  onRequestSubmit,
  buildSubmitResult,
  onValidationError,
}: UseChemSearchModalSubmitArgs): UseChemSearchModalSubmitResult {
  const [submitError, setSubmitError] = useState<string | null>(null);

  const clearSubmitError = useCallback((): void => {
    setSubmitError(null);
  }, []);

  const handleSubmit = useCallback((): void => {
    const result = buildSubmitResult();
    if (result.type === 'validation_error') {
      setSubmitError(result.message);
      onValidationError?.(result);
      return;
    }

    setSubmitError(null);
    if (toggleHighlightParams.showToggleHighlightOption) {
      toggleHighlightParams.setShouldHighlightSubstructure(highlightChecked);
    }
    preferences.set(ChemistryPreferences.CHEM_SEARCH_TYPE, chemSearchOperator);
    if (result.minSimilarityToSave != null) {
      preferences.set(ChemistryPreferences.MIN_SIMILARITY, result.minSimilarityToSave);
    }
    onRequestSubmit(result.submit);
  }, [
    buildSubmitResult,
    chemSearchOperator,
    highlightChecked,
    onRequestSubmit,
    onValidationError,
    toggleHighlightParams,
  ]);

  return {
    submitError,
    clearSubmitError,
    handleSubmit,
  };
}
